<script>
export default {
  name: "ConfiguratorWidget",
};
</script>

<script setup>
import { unref, defineProps, defineEmits, computed } from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import Caret from "@/components/shared/icons/Caret.vue";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import GetClassesSelect from "@/components/program-manager/sessions/components/Academics/GetClassesSelect.vue";
import ConfiguratorWidgetChild from "../ConfiguratorWidget";
import { useVModel } from "@vueuse/core";
import { baseClassSelectionRule } from "@/components/program-manager/sessions/constants";
import {
  configuratorColors,
  configuratorConditionals,
} from "@/components/program-manager/sessions/constants";
import { uuid } from "vue-uuid";
import TrashCan from "@/components/shared/icons/TrashCan.vue";

const props = defineProps({
  modelValue: {
    type: Object,
  },
  filters: {
    type: Object,
    required: true,
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
  depth: {
    type: Number,
    default: 1,
  },
});

const reachedMaxDepth = computed(() => {
  return props.depth > 1;
});

const emit = defineEmits(["update:modelValue"]);
const rule = useVModel(props, "modelValue", emit);

const addClass = () => {
  unref(rule).library_classes.push({ value: undefined });
};
const addGroup = () => {
  if (!reachedMaxDepth.value) {
    unref(rule).rules.push({
      value: { ...baseClassSelectionRule(), id: uuid.v4() },
    });
  }
};

const enabled = computed(() => {
  let classesFound = 0;
  if (rule?.value?.library_classes?.length) {
    for (const library_class of rule.value.library_classes) {
      if (library_class?.value?.id) {
        classesFound = 1;
        break;
      }
    }
  }
  return classesFound;
});
</script>

<template>
  <div class="flex flex-col col-span-2 w-full">
    <div v-if="showLabel" class="block text-xs font-semibold text-gray-700">
      Configurator
    </div>
    <div class="flex w-full">
      <div
        class="w-[110px] m-h-[220px] split-color flex flex-column justify-center items-center"
      >
        <Listbox v-model="rule.conjunction">
          <div>
            <ListboxButton
              :style="{
                'background-color':
                  configuratorColors[depth % 2].middle[enabled],
              }"
              class="relative w-[80px] h-[34px] bg-blue-800 cursor-pointer rounded-full text-white py-2 pl-3 text-center uppercase flex items-center justify-around"
            >
              <div>{{ rule.conjunction }}</div>
              <Caret class="w-[13px] mr-3 white-caret" />
            </ListboxButton>
            <ListboxOptions
              class="absolute z-10 mt-1 max-h-60 w-[80px] overflow-auto rounded-md bg-white py-1 text-base text-black shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm cursor-pointer"
            >
              <ListboxOption
                v-for="conditional in configuratorConditionals"
                :key="conditional.id"
                v-slot="{ active, selected }"
                as="template"
                :value="conditional.id"
              >
                <li
                  :class="['relative select-none py-2 text-center uppercase']"
                >
                  <span :class="[selected ? 'font-semibold' : 'font-normal']">{{
                    conditional.name
                  }}</span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </div>
        </Listbox>
      </div>
      <div
        class="w-full bg-blue-425 pl-2.5"
        :style="{ 'background-color': configuratorColors[depth % 2].right }"
      >
        <BasicTextInput
          v-model="rule.name"
          field-label="Track Name"
          outer-classes="flex items-center my-3 pl-12 mr-1 lg:mr-16"
          field-label-classes="common-label-text w-[134px] text-nowrap"
        />
        <div
          v-for="(libraryClass, index) in rule.library_classes"
          class="pl-12"
        >
          <GetClassesSelect
            v-model="libraryClass.value"
            class="my-4"
            :filters="props.filters"
            :on-remove="() => rule?.library_classes?.splice(index, 1)"
          />
        </div>
        <div
          v-for="(childRule, index) in rule.rules"
          class="mb-2 mr-8 flex align-baseline items-center w-full"
        >
          <ConfiguratorWidgetChild
            v-model="childRule.value"
            :filters="props.filters"
            :depth="props.depth + 1"
          />
          <TrashCan
            class="w-[18px] mx-6 cursor-pointer"
            @click="() => rule.rules.splice(index, 1)"
          />
        </div>

        <div class="flex mb-2 pl-12">
          <div
            class="flex text-teal-900 items-center mr-6 text-xs font-semibold antialiased cursor-pointer uppercase"
            @click="addClass"
          >
            <PlusIcon class="mr-1 w-6 h-6" />
            <div>Class</div>
          </div>
          <div
            v-if="!reachedMaxDepth"
            class="flex text-teal-900 items-center text-xs font-semibold antialiased cursor-pointer uppercase"
            @click="addGroup"
          >
            <PlusIcon class="mr-1 w-6 h-6" />
            <div>Group</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.split-color {
  background: linear-gradient(
    to right,
    v-bind(configuratorColors[depth % 2].left) 48%,
    v-bind(configuratorColors[depth % 2].middle[enabled]) 48% 51%,
    v-bind(configuratorColors[depth % 2].right) 51%
  );
}
.white-caret path {
  fill: white;
}
</style>
